/**
 * Typed HTTP client built on top of `shared/contracts.ts`.
 *
 * `apiFetch(contract, opts)` returns a value typed as `z.infer<contract.response>`.
 * The response is validated at runtime against the declared schema, so a
 * server-side shape change that diverges from the contract surfaces as a
 * descriptive error in the browser console rather than as a silent UI bug.
 *
 * See `docs/contracts.md` for the workflow.
 */
import { z } from "zod";
import { buildPath, type ApiContract } from "@shared/contracts";
import { addBreadcrumb, captureAuthFailure, captureException, sanitizeUrlForLogging } from "@/lib/sentry";

export interface ApiFetchOptions<C extends ApiContract> {
  params?: Record<string, string | number>;
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: z.input<NonNullable<C["body"]>>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export class ApiFetchError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message: string,
    public readonly body?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiFetchError";
  }
}

/**
 * Returns the first 8 hex characters of a request ID formatted for display in
 * a user-facing toast: "Error ID: fb968c91".  Returns undefined when no request
 * ID is available so callers can omit the line entirely.
 *
 * Usage in a toast onError handler:
 *   const short = formatShortRequestId(err instanceof ApiFetchError ? err.requestId : undefined);
 *   toast({ title: "Save failed", description: short ? `Error ID: ${short}` : err.message });
 */
export function formatShortRequestId(requestId?: string): string | undefined {
  if (!requestId) return undefined;
  const hex = requestId.replace(/-/g, "");
  return hex.slice(0, 8) || undefined;
}

export class ApiContractError extends Error {
  constructor(
    public readonly contractName: string,
    public readonly issues: z.ZodIssue[],
  ) {
    super(
      `Response for "${contractName}" did not match contract: ${issues
        .map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`)
        .join("; ")}`,
    );
    this.name = "ApiContractError";
  }
}

export async function apiFetch<C extends ApiContract>(
  contract: C,
  options: ApiFetchOptions<C> = {},
): Promise<z.infer<C["response"]>> {
  const path = buildPath(contract.path, options.params);
  const url = options.query
    ? `${path}?${new URLSearchParams(
        Object.entries(options.query)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)] as [string, string]),
      ).toString()}`
    : path;

  const headers: Record<string, string> = { ...(options.headers || {}) };
  let bodyInit: BodyInit | undefined;
  if (options.body !== undefined && contract.method !== "GET") {
    headers["Content-Type"] ??= "application/json";
    bodyInit = JSON.stringify(options.body);
  }

  const res = await fetch(url, {
    method: contract.method,
    headers,
    body: bodyInit,
    credentials: "include",
    signal: options.signal,
  });

  let payload: unknown = undefined;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // intentionally silent
      payload = text;
    }
  }

  if (!res.ok) {
    const payloadObj =
      payload !== null && typeof payload === "object"
        ? (payload as Record<string, unknown>)
        : null;
    const message =
      (payloadObj && "error" in payloadObj ? String(payloadObj.error) : null) ||
      `${res.status} ${res.statusText}`;
    const requestId =
      (payloadObj && typeof payloadObj.requestId === "string"
        ? payloadObj.requestId
        : undefined) ?? res.headers.get("x-request-id") ?? undefined;
    const err = new ApiFetchError(res.status, url, message, payload, requestId);
    if (res.status === 401) {
      captureAuthFailure(url, { contract: contract.name });
    } else {
      const isSlotConflict =
        res.status === 409 &&
        payloadObj !== null &&
        payloadObj.slotConflict === true;
      if (!isSlotConflict) {
        captureException(
          err,
          { url, status: res.status, contract: contract.name },
          requestId ? { request_id: requestId } : undefined,
        );
      }
    }
    throw err;
  }

  const parsed = contract.response.safeParse(payload);
  if (!parsed.success) {
    const err = new ApiContractError(contract.name, parsed.error.issues);
    const safeUrl = sanitizeUrlForLogging(url);
    addBreadcrumb(
      `[contract] Response for "${contract.name}" failed schema validation`,
      {
        url: safeUrl,
        contract: contract.name,
        issues: parsed.error.issues
          .slice(0, 5)
          .map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`),
      },
    );
    captureException(err, { url: safeUrl, contract: contract.name });
    throw err;
  }
  return parsed.data as z.infer<C["response"]>;
}
